/**
 * Ordering and paging a table of rows.
 *
 * Extracted from the component because these two decisions are the ones that
 * break quietly: a comparator that puts blanks first fills the first page with
 * dashes, and a page window that is off by one hides the last page. Neither
 * throws, and both look plausible on the screen that has three rows.
 */

/**
 * Compare two values, keeping absent ones at the end whichever way the arrow
 * points.
 *
 * # Why blanks do not simply sort as empty strings
 *
 * Because absent is not "smallest", it is "not applicable". Two thirds of the
 * people in this corpus have no telephone number; ordering ascending on that
 * column with blanks treated as empty strings gives a first page of dashes and
 * buries the rows the customer sorted in order to see.
 *
 * So present values are ordered among themselves and absent ones follow. The
 * arrow reverses the first group and never moves the second.
 */
export function compareBlanksLast(
  left: string,
  right: string,
  collator: Intl.Collator,
  ascending: boolean
): number {
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;

  return ascending ? collator.compare(left, right) : collator.compare(right, left);
}

/**
 * How many pages a set of rows needs.
 *
 * At least one, so an empty table is page one of one rather than page one of
 * zero — which reads as a fault and makes every "is this the last page" test
 * false.
 */
export function pageCountFor(total: number, perPage: number): number {
  if (perPage <= 0) return 1;
  return Math.max(1, Math.ceil(total / perPage));
}

/**
 * The page numbers to draw, as a window around the current one.
 *
 * A company can have several hundred people. Every page as a button is forty
 * buttons, which is not navigation — so a fixed-width window slides along, and
 * it stays full width at both ends rather than shrinking to two buttons on the
 * last page.
 */
export function pageWindow(current: number, pageCount: number, span = 5): number[] {
  if (pageCount <= 1) return [1];

  const width = Math.min(span, pageCount);
  let first = Math.max(1, current - Math.floor(width / 2));
  const last = Math.min(pageCount, first + width - 1);

  // Pull the window back when it runs off the end, so the last page always
  // shows `width` buttons rather than however many happened to be left.
  first = Math.max(1, last - width + 1);

  return Array.from({ length: last - first + 1 }, (_, i) => first + i);
}
